import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Divider, Heading, Skeleton, Text } from '@indeed/ifl-components';
import { Lock } from '@indeed/ifl-icons';
import { Alert9 } from '@indeed/indeed-design-system-illustrations-spot';
import {
    FieldTelemetryProvider,
    InformationQuestion,
    MultiFieldQuestionReviewList,
} from '@indeed/jspl-questions';
import { FieldValidationProvider, useFieldValidation } from '@indeed/jspl-validation';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { StickyActionFooter } from '../../../components/StickyActionFooter/StickyActionFooter';
import JobInfoHeader from '../components/JobInfoHeader';
import DraftApplicationTabs from '../components/DraftApplicationTabs';
import { CollapsibleQuestion } from '../../../components/CollapsibleQuestion';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { usePageFlow } from '../../../lib/pageFlow';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { useApplyForMeCopy } from '../../auto-apply-setup/hooks/useApplyForMeCopy';
import GettextFactory from '../../../util/GettextFactory';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';
import type { ScreenerQuestionContextQuestion } from '../../../__generated__/graphql-base-types';
import {
    useGetAutoApplyScreenerQuestionsQuery,
    useSaveSQApplicationDataMutation,
    type GetAutoApplyScreenerQuestionsQuery,
} from './__generated__/QuestionsPage.generated';
import { useGetJobInfoHeaderDataQuery } from '../components/__generated__/JobInfoHeader.generated';
import { ScreenerQuestionField } from './ScreenerQuestionField';
import { useDeleteAutoApplyApplicationDraft } from '../hooks/useDeleteAutoApplyApplicationDraft';
import { getIriId } from '../../../lib/utils';
import { ROOT_TRACKING_KEY_HEADER } from '../../../lib/apollo/headers';
import {
    addFailedDraftId,
    buildLoadedAnswers,
    buildScreenerAnswers,
    getDisplayValues,
    getQuestionLabel,
    isAnswerable,
    isAnswered,
    isMissingRequiredAnswer,
} from './screenerQuestionUtils';
import type { AnswerValue, ScreenerFileAnswer } from './screenerQuestionUtils';
import { buildFileUploadUrl, uploadFile } from '../hooks/fileUpload';
import { useDeleteDraftNavigation } from '../hooks/useDeleteDraftNavigation';
import { ScreenerQuestionITV } from './itv/ScreenerQuestionITV';

/** The draft's SmartApplyApplicationState node, narrowed out of the polymorphic `node(id)` union. */
type SavedApplicationStateNode = Extract<
    NonNullable<GetAutoApplyScreenerQuestionsQuery['smartApplyApplicationState']>,
    { __typename?: 'SmartApplyApplicationState' }
>;

/**
 * Whether the draft's saved answers actually loaded. A type guard (not a bare boolean) so a single
 * check both gates `loadFailed` and narrows the node for reading `questionsAndAnswers`.
 */
const isSavedApplicationState = (
    node: GetAutoApplyScreenerQuestionsQuery['smartApplyApplicationState'],
): node is SavedApplicationStateNode => node?.__typename === 'SmartApplyApplicationState';

interface QuestionFieldsProps {
    questions: ScreenerQuestionContextQuestion[];
    answers: Record<string, AnswerValue>;
    /** The answers as loaded (prefills + saved), without live edits — drives the collapsed preview. */
    loadedAnswers: Record<string, AnswerValue>;
    onAnswer: (name: string, value: AnswerValue) => void;
    /** FILE only: upload a newly selected file for the given question. */
    onFileSelect: (name: string, file: File) => void;
    /** FILE only: remove the file at the given index for the given question. */
    onFileRemove: (name: string, index: number) => void;
}

/**
 * The question list. Sits below `FieldValidationProvider` so it can see which fields jspl
 * currently considers invalid — the page above the provider cannot.
 *
 * That is what decides whether a question may be folded away. A collapsed `CollapsibleQuestion`
 * unmounts its field, which takes the error off screen while it still blocks the CTA, so a
 * question is held open while its answer is unacceptable: missing when required (page state, since
 * an unmounted field tells jspl nothing) or carrying a jspl error. Blocking the fold is what keeps
 * a question from getting stuck in a failed state the jobseeker can neither see nor clear.
 */
const QuestionFields: React.FunctionComponent<QuestionFieldsProps> = ({
    questions,
    answers,
    loadedAnswers,
    onAnswer,
    onFileSelect,
    onFileRemove,
}) => {
    const gt = GettextFactory();
    const { fields } = useFieldValidation();

    const renderQuestion = (question: ScreenerQuestionContextQuestion) => {
        switch (question.type) {
            case 'INFORMATION':
                return <InformationQuestion text={question.textHtml} level={3} />;

            // The formField for MULTIFIELD is a marker with no answer data — entries come from
            // MultiFieldAnswerSmartApplyApplicationState. Read-only until that is wired up.
            case 'MULTIFIELD':
                return (
                    <MultiFieldQuestionReviewList
                        label={question.groupLabel ?? question.labelHtml}
                        helperText={question.textHtml || undefined}
                        addItemButtonLabel={question.addButtonLabel ?? gt.gettext('Add another')}
                        items={[]}
                    />
                );

            default:
                return (
                    <CollapsibleQuestion
                        questionLabel={getQuestionLabel(question)}
                        type={question.type}
                        prefilled={isAnswered(question, loadedAnswers[question.name])}
                        heldOpen={
                            isMissingRequiredAnswer(question, answers) ||
                            Boolean(fields[question.name]?.errorText)
                        }
                        values={getDisplayValues(question, answers[question.name])}
                        options={question.options}
                    >
                        <ScreenerQuestionField
                            question={question}
                            value={answers[question.name] ?? []}
                            onChange={(value) => onAnswer(question.name, value)}
                            onFileSelect={(file) => onFileSelect(question.name, file)}
                            onFileRemove={(index) => onFileRemove(question.name, index)}
                        />
                    </CollapsibleQuestion>
                );
        }
    };

    return (
        <FieldTelemetryProvider>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {questions.map((question, index) => (
                    <React.Fragment key={question.name}>
                        {index > 0 && <Divider />}
                        {renderQuestion(question)}
                    </React.Fragment>
                ))}
            </Box>
        </FieldTelemetryProvider>
    );
};

interface QuestionsFooterProps {
    /** CTA label — "Review application" on the main spine, "Update" on the `/questions/edit` side-trip. */
    label: string;
    /** A save (SaveSQApplicationData) is in flight, so the CTA is disabled to prevent double-submit. */
    saving?: boolean;
    /** A FILE upload is in flight, so the CTA is disabled until it resolves (no tokenless save). */
    uploading?: boolean;
    /** Extra disable condition with no spinner (e.g. the flow's step list still loading). */
    disabled?: boolean;
    /**
     * Bring the Application tab forward. Called before exposing validation errors so a submit from the
     * Job details tab surfaces the errors where the jobseeker can see them (the questions render in the
     * Application tab). No-op for the plain footer, which has no validation.
     */
    onGoToApplicationTab?: () => void;
    /** Persist (if applicable) and leave the page. */
    onSubmit: () => void;
}

/**
 * The single footer CTA, matching the design (no Previous / Next — going back to a previous draft's
 * review is intentionally not offered from here). Plain (no validation), used by the load-error
 * screen, which renders no `FieldValidationProvider` and has nothing to answer, so its CTA advances
 * unconditionally.
 */
const QuestionsFooterButton: React.FunctionComponent<QuestionsFooterProps> = ({
    label,
    saving,
    uploading,
    disabled,
    onSubmit,
}) => {
    // A save disables and shows the spinner; an in-flight upload or a still-loading flow only disable
    // (no spinner) so the jobseeker can't advance with a tokenless file or an incomplete step list.
    const busy = saving || uploading || disabled;

    return (
        <Button
            variant="primary"
            size="lg"
            onClick={onSubmit}
            disabled={busy}
            loading={saving}
            sx={{ inlineSize: '100%' }}
        >
            {label}
        </Button>
    );
};

/**
 * The loaded-page footer CTA, gated on jspl validity. A separate component (not inlined into the
 * page) because it calls `useFieldValidation`, which only works below the `FieldValidationProvider`
 * the page renders — the page component itself sits above the provider and can't read it.
 *
 * jspl's validators cover `required` and the per-type constraints, and `QuestionFields` guarantees
 * any question with an unacceptable answer is held open (its field mounted and registered), so
 * `exposeErrors` always has something to mark and focus.
 */
const ValidatedQuestionsFooter: React.FunctionComponent<QuestionsFooterProps> = ({
    label,
    saving,
    uploading,
    disabled,
    onGoToApplicationTab,
    onSubmit,
}) => {
    const { valid, exposeErrors, fields } = useFieldValidation();
    const busy = saving || uploading || disabled;

    // jspl seeds form validity to `false` and only flips it once a registered field validates.
    // A page where every question is prefilled collapses them all, so no jspl field ever mounts,
    // nothing registers, and `valid` would stay false forever — blocking the CTA with nothing to
    // fix. Only let jspl arbitrate once it actually has fields to speak for.
    const hasRegisteredFields = Object.keys(fields).length > 0;

    const handleClick = () => {
        const blocked = hasRegisteredFields && !valid;
        // If the submit is blocked from the Job details tab, bring the Application tab forward first so
        // the errors `exposeErrors` reveals are actually on screen. On a valid submit we skip it — the
        // page is navigating away anyway.
        if (blocked) onGoToApplicationTab?.();
        // Reveals every field error and focuses the first, so a blocked CTA explains itself.
        exposeErrors();
        if (blocked) return;
        onSubmit();
    };

    return (
        <Button
            variant="primary"
            size="lg"
            onClick={handleClick}
            disabled={busy}
            loading={saving}
            sx={{ inlineSize: '100%' }}
        >
            {label}
        </Button>
    );
};

/**
 * Questions Page for Auto Apply.
 *
 * Serves two routes, both mirroring the review page's layout — the screener questions render in the
 * "Application" tab under the job info header:
 *   - `/questions` is a main-spine step shown before a draft's review when its
 *     `unfilledScreenerQuestionCount` is > 0; its CTA is "Review application" and advances the flow.
 *   - `/questions/edit` is the side-trip opened from the review page's screener-questions edit; its
 *     CTA is "Update" and returns to that draft's review without dropping it on a transient error.
 */
const QuestionsPage: React.FC = () => {
    const gt = GettextFactory();
    const navigate = useNavigate();
    const { draftId } = useParams<{ draftId: string }>();
    const location = useLocation();
    const eventLogger = useEventLogger();
    const copy = useApplyForMeCopy();
    const { locale, smartApplyBaseUrl, graphqlEndpoint } = useInitialState();
    const { goForward, allSteps, loading: flowLoading } = usePageFlow();
    const [searchParams, setSearchParams] = useSearchParams();
    // Which tab DraftApplicationTabs is showing (0 = Application, 1 = Job details). Controlled here so
    // the footer can bring the Application tab forward before validating — the questions render in the
    // Application tab, so a submit from Job details would otherwise expose errors off screen.
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);

    const inEditSubFlow = location.pathname.endsWith('/questions/edit');

    // Whether this draft is already in the durable failedDraftIds param — used to keep the
    // load-error effect idempotent (mirrors ReviewPage), so it logs/marks at most once.
    const isCurrentDraftFailed = draftId
        ? (searchParams.get('failedDraftIds')?.split(',').filter(Boolean) ?? []).includes(draftId)
        : false;

    // The draft's iaUid, the root tracking key the smart-apply screener subgraphs require as the
    // `indeed-root-tracking-key` header. Fetched first (and separately) because the screener query
    // below cannot header itself with a value it is still fetching. Reuses GetJobInfoHeaderData — the
    // same query the JobInfoHeader on this page runs, with the same variables, so Apollo dedupes it
    // to a single request; the result is also passed down to JobInfoHeader as its `fragment`.
    const {
        data: draftData,
        loading: draftLoading,
        error: draftError,
    } = useGetJobInfoHeaderDataQuery({
        variables: { input: { applicationDraftId: draftId ?? '', skipSourceValidation: true } },
        skip: !draftId || isCurrentDraftFailed,
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
    });
    const draft = draftData?.findReviewBeforeSubmitDraft?.draft ?? undefined;
    const iaUid = draft?.iaUid ?? undefined;

    // The saved-answers node id must be the SmartApplyApplicationState IRI, not the bare draft id —
    // the bare id fails IRI parsing in graphql-nodes-api and the node comes back null. `getIriId`
    // throws on an empty id/URL, so guard on draftId (the query is skipped without one) and fall back
    // to a valid host when graphqlEndpoint is absent (mock / local-dev, where the host is immaterial —
    // handlers match by operation name, not node id).
    const nodeId = draftId
        ? getIriId({ draftId, oneGraphUrl: graphqlEndpoint ?? 'https://apis.indeed.com/graphql' })
        : '';

    // no-cache: these screener-question types normalize poorly (non-unique ids, a nested
    // `formField` union) and nothing else reads them, so the cache would only add stale- or
    // mangled-read risk with no upside.
    // Gate on iaUid: both roots hit subgraphs that require the `indeed-root-tracking-key` header, so
    // there is nothing to send until the draft's iaUid is loaded.
    // Skip once the draft is failed: a failed draft is terminal (mirrors ReviewPage), so we do not
    // retry — otherwise a successful reload would let the jobseeker re-answer a draft that is still
    // excluded from submission (`failedDraftIds` is one-way).
    const { data, loading, error } = useGetAutoApplyScreenerQuestionsQuery({
        variables: {
            input: { draftId: draftId ?? '' },
            nodeId,
        },
        skip: !draftId || isCurrentDraftFailed || !iaUid,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
        context: { headers: { [ROOT_TRACKING_KEY_HEADER]: iaUid } },
    });

    const [saveAnswers, { loading: saving }] = useSaveSQApplicationDataMutation();
    const deleteAutoApplyApplicationDraft = useDeleteAutoApplyApplicationDraft();

    // Delete-and-advance: fired by the JobInfoHeader trash button, awaits the delete then walks to
    // the next draft (questions-aware). Shared with ReviewPage so both pages behave identically —
    // previously QuestionsPage passed nothing, so the trash button deleted silently with no feedback.
    const { onDelete: handleDeleteDraft, isDeleting } = useDeleteDraftNavigation();

    // The generated operation type differs from the base only by an optional __typename that
    // Apollo always populates at runtime, so the render path can treat it as the base type.
    const questions = React.useMemo(
        () =>
            (data?.smartApplyContext?.screenerQuestionContext?.screenerQuestions ??
                []) as ScreenerQuestionContextQuestion[],
        [data],
    );

    // The job's Indeed Apply apiToken, sent as `IA-API-KEY` when uploading FILE answers (see
    // hooks/fileUpload.ts). Same per-job token the in-apply flow feeds mosaic as
    // `commonMetadata.apiToken`; smart-apply-bff rejects the upload with 403 without it.
    const employerApiToken = data?.smartApplyContext?.jobContext?.employer.apiToken;

    // Answers the jobseeker previously saved on this draft (SmartApplyApplicationState), overlaid onto
    // the smartApplyContext prefills below.
    const savedState = data?.smartApplyApplicationState;
    const savedAnswersLoaded = isSavedApplicationState(savedState);
    const savedScreener = React.useMemo(
        () =>
            isSavedApplicationState(savedState)
                ? (savedState.questionsAndAnswers?.screener ?? [])
                : [],
        [savedState],
    );

    // The screener query is gated on iaUid, so if the draft read settles without one — an outright
    // error, OR a success whose draft carries no iaUid — the questions can never load. Treat either
    // as a load failure (drop the draft) rather than falling through to an empty form whose CTA
    // would wave the draft past its still-unanswered questions. `!draftLoading` keeps the in-flight
    // window from being misread; a skipped read (already-failed draft / no draftId) is handled by the
    // isCurrentDraftFailed render branch and the effect's own guards, so no extra skip check is needed.
    const draftLoadFailed = !draftLoading && !iaUid;

    // With `errorPolicy: 'all'` a partial failure still returns `data`, so `!data` is not a reliable
    // "failed to load" signal. Treat it as failed when the questions did not load, or when the saved
    // answers read failed — otherwise `savedScreener` silently falls back to `[]` and the CTA would
    // overwrite the jobseeker's persisted answers with prefills.
    const loadFailed =
        draftLoadFailed || (!!error && (questions.length === 0 || !savedAnswersLoaded));

    // The answers the form loads with: prefills overlaid with saved draft answers (saved wins),
    // matching the mosaic module. Keep only the jobseeker's edits in state and layer them on top —
    // nothing to "seed", so no ref/effect/timing to get wrong, and an edit can't be lost when the
    // query settles because edits live apart and always win.
    const loadedAnswers = React.useMemo(
        () => buildLoadedAnswers(questions, savedScreener),
        [questions, savedScreener],
    );
    const [edits, setEdits] = React.useState<Record<string, AnswerValue>>({});
    const answers = React.useMemo(() => ({ ...loadedAnswers, ...edits }), [loadedAnswers, edits]);
    const setAnswer = (name: string, value: AnswerValue) =>
        setEdits((previous) => ({ ...previous, [name]: value }));

    // Latest merged answers for async upload callbacks to read without a stale closure — used to tell
    // whether a still-uploading file was removed by the jobseeker before its upload settled.
    const answersRef = React.useRef(answers);
    answersRef.current = answers;

    // Updates one FILE question's answer, reading the latest merged value (edits over prefill) so
    // concurrent uploads don't clobber each other. `loadedAnswers` is the fallback when the question
    // has no edit yet.
    const updateFiles = React.useCallback(
        (name: string, update: (files: ScreenerFileAnswer[]) => ScreenerFileAnswer[]) => {
            setEdits((previous) => {
                const current = (previous[name] ??
                    loadedAnswers[name] ??
                    []) as ScreenerFileAnswer[];
                return { ...previous, [name]: update(current) };
            });
        },
        [loadedAnswers],
    );

    // A FILE answer is not persistable until its token resolves, so hold the CTA while any upload
    // is in flight (jspl does not gate on upload state on its own).
    const uploadInProgress = React.useMemo(
        () =>
            questions.some(
                (q) =>
                    q.type === 'FILE' &&
                    (answers[q.name] as ScreenerFileAnswer[] | undefined)?.some(
                        (file) => file.uploading,
                    ),
            ),
        [questions, answers],
    );
    const [uploadError, setUploadError] = React.useState(false);

    // Optimistically add a placeholder, upload, then swap in the tokened answer (or drop the
    // placeholder on failure) — mirrors mosaic's FileUploadFormElement.onSelect.
    const handleFileSelect = React.useCallback(
        (name: string, file: File) => {
            setUploadError(false);
            const placeholder: ScreenerFileAnswer = { name: file.name, uploading: true };
            updateFiles(name, (files) => [...files, placeholder]);

            uploadFile(
                {
                    uploadUrl: buildFileUploadUrl(smartApplyBaseUrl),
                    uploadCategory: 'screener_question',
                    iaUid,
                    apiKey: employerApiToken,
                },
                file,
            )
                .then((uploaded) => {
                    updateFiles(name, (files) =>
                        files.map((entry) => (entry === placeholder ? uploaded : entry)),
                    );
                })
                .catch((err) => {
                    // If the jobseeker removed the still-uploading file before it failed, there is
                    // nothing to roll back and surfacing an error would be confusing — stay quiet.
                    const stillPending = (
                        (answersRef.current[name] as ScreenerFileAnswer[] | undefined) ?? []
                    ).includes(placeholder);
                    if (!stillPending) return;

                    updateFiles(name, (files) => files.filter((entry) => entry !== placeholder));
                    setUploadError(true);
                    eventLogger.logReviewError?.({
                        currentPage: location.pathname,
                        errorType: { string: 'screener-questions-file-upload-error' },
                        errorMessage: {
                            string: err instanceof Error ? err.message : 'Failed to upload file',
                        },
                        draftId: AvroOptional.string(draftId),
                        graphqlOperation: { string: 'ScreenerFileUpload' },
                    });
                });
        },
        [
            updateFiles,
            eventLogger,
            location.pathname,
            draftId,
            smartApplyBaseUrl,
            iaUid,
            employerApiToken,
        ],
    );

    const handleFileRemove = React.useCallback(
        (name: string, index: number) => {
            updateFiles(name, (files) => files.filter((_, i) => i !== index));
        },
        [updateFiles],
    );

    const visibleQuestions = questions.filter((q) => q.type !== 'PAGEBREAK');
    // Counts what loaded unanswered (prefills + saved), not live edits, so the heading matches the
    // collapse logic and stays put as the jobseeker fills answers in rather than ticking down.
    const unansweredCount = questions.filter(
        (q) => isAnswerable(q.type) && !isAnswered(q, loadedAnswers[q.name]),
    ).length;

    const logButtonClick = (button: string) => {
        eventLogger.logReviewButtonClick?.({
            currentPage: location.pathname,
            button,
            draftId: AvroOptional.string(draftId),
        });
    };

    // Drop this draft from the batch submission by marking it in the durable `failedDraftIds` URL
    // param — the same one ReviewPage uses, which ConfirmationPage reads to exclude drafts from the
    // final `Promise.allSettled` submit. Reused (not a new param) so the exclusion is automatic.
    const markDraftFailed = React.useCallback(() => {
        if (!draftId) return;
        setSearchParams(
            (previous) => new URLSearchParams(addFailedDraftId(previous.toString(), draftId)),
            { replace: true },
        );
    }, [draftId, setSearchParams]);

    // The next step to advance to when this draft is dropped: the step right after this draft's
    // review in the flow's step list — the next draft's first step (its questions page or review,
    // already gated by the flow), or the confirmation page when this is the last draft. Reusing
    // `allSteps` lets a failed draft skip past its own (now failed) review without re-deriving the
    // questions-vs-review decision here.
    const nextDraftDestination = React.useMemo(() => {
        const reviewIndex = allSteps.findIndex((step) =>
            step.routes.includes(`/auto-apply/${draftId}/review`),
        );
        return reviewIndex >= 0
            ? (allSteps[reviewIndex + 1]?.routes[0] ?? '/auto-apply/confirmation')
            : '/auto-apply/confirmation';
    }, [allSteps, draftId]);

    // A failed draft has nothing to review, so advance straight to the next draft (or confirmation),
    // baking the failed id into the destination. Marking-then-navigating would race — the navigation
    // would carry the stale search and drop the mark — so it is one navigate.
    const continueWithDraftFailed = () => {
        if (!draftId) return;
        void navigate(`${nextDraftDestination}?${addFailedDraftId(location.search, draftId)}`, {
            replace: true,
        });
    };

    // Return to this draft's review — the edit side-trip's exit (no drop).
    const goToReview = () => {
        void navigate(`/auto-apply/${draftId}/review${location.search}`, { replace: true });
    };

    // A questions load failure prevents the jobseeker from answering. On the main spine that drops
    // the draft from the batch (mirrors ReviewPage's failed-draft handling); on the edit side-trip
    // it must not, so the draft is left healthy. The error screen still offers its CTA to continue.
    React.useEffect(() => {
        if (loadFailed && draftId && !isCurrentDraftFailed) {
            eventLogger.logReviewError?.({
                currentPage: location.pathname,
                errorType: { string: 'screener-questions-load-error' },
                errorMessage: {
                    string:
                        error?.message ??
                        draftError?.message ??
                        (draftLoadFailed
                            ? 'Draft loaded without an iaUid'
                            : 'Failed to load screener questions'),
                },
                draftId: AvroOptional.string(draftId),
                graphqlOperation: {
                    string: draftLoadFailed
                        ? 'GetJobInfoHeaderData'
                        : 'GetAutoApplyScreenerQuestions',
                },
            });
            if (!inEditSubFlow) {
                markDraftFailed();
                // Mirror ReviewPage: a permanent (server GraphQL) error won't recover on retry, so
                // also delete the draft server-side. A temporary (network/timeout) error — or a
                // success that simply carried no iaUid — leaves the draft alone; it is only dropped
                // from this batch (failedDraftIds) and can be retried in a later session.
                //
                // Each error only counts as permanent when it actually left its data unloaded, so a
                // partial failure never deletes an intact draft:
                //   - screener: the questions themselves failed (`questions.length === 0`). A partial
                //     failure — questions load but the saved-answers node fails
                //     (`!savedAnswersLoaded`) — still populates `error.graphQLErrors`, but the draft
                //     is fine, so it is only dropped from the batch.
                //   - draft: the read left no iaUid (`draftLoadFailed`). With `errorPolicy: 'all'`
                //     GetJobInfoHeaderData can return a usable draft (iaUid resolved) alongside field
                //     errors on nullable fields; `draftError.graphQLErrors` is populated then too, but
                //     the draft loaded, so gating on `draftLoadFailed` keeps it from being deleted.
                const isPermanent =
                    (questions.length === 0 && (error?.graphQLErrors.length ?? 0) > 0) ||
                    (draftLoadFailed && (draftError?.graphQLErrors.length ?? 0) > 0);
                if (isPermanent) {
                    // Auto-delete: system cleanup of a draft that can't load, not the jobseeker
                    // rejecting the job. Pass null so the draft is removed WITHOUT disliking the job —
                    // only user-clicked deletes (JobInfoHeader / ConfirmationPage) dislike.
                    void deleteAutoApplyApplicationDraft(draftId, null).catch(() => {});
                }
            }
        }
    }, [
        loadFailed,
        error,
        draftError,
        draftLoadFailed,
        draftId,
        draft,
        questions.length,
        isCurrentDraftFailed,
        inEditSubFlow,
        markDraftFailed,
        deleteAutoApplyApplicationDraft,
        eventLogger,
        location.pathname,
    ]);

    /**
     * Saves the current answers to the draft (SaveSQApplicationData), then leaves the page. This is
     * the click handler for the only two CTAs that save — Next on the main spine and Update on the
     * `/questions/edit` side-trip — so it owns the edit-vs-main branching itself and the callers
     * stay dumb. Uses the lightweight ITV pattern (verify the mutation returned the saved screener
     * before advancing). A mosaic-style round-trip compare then runs log-only after navigation
     * (not awaited): mismatches are recorded, but a valid persist is not rejected. Where it goes:
     *   - success → the edit side-trip returns to review; the main spine walks the flow (`goForward`).
     *   - failure → the edit side-trip just returns to review (a transient error must not drop an
     *     already-reviewed draft); the main spine drops the draft from the batch and goes to review.
     *   - nothing loaded (the load-error screen wires its CTA directly, not this).
     */
    const persistAndNavigate = async () => {
        if (!draftId) return;

        const onFailure = () => {
            if (inEditSubFlow) goToReview();
            else continueWithDraftFailed();
        };
        const onSuccess = () => {
            if (inEditSubFlow) goToReview();
            else void goForward();
        };

        // Never save when the load didn't fully succeed — saving from prefill-only state
        // (savedScreener is []) would overwrite the jobseeker's persisted answers, the exact harm
        // loadFailed guards against.
        if (loadFailed) {
            onFailure();
            return;
        }
        // Nothing answerable loaded (and no error) — just advance, nothing to persist.
        if (questions.length === 0) {
            onSuccess();
            return;
        }

        try {
            const result = await saveAnswers({
                variables: {
                    draftInput: { id: draftId },
                    sqInput: {
                        applicationDraftId: draftId,
                        // `edits` keys are the questions the jobseeker changed — lets buildScreenerAnswers
                        // tell an explicit FILE clear (send `files: []`) from an untouched prefill (omit).
                        screener: buildScreenerAnswers(
                            questions,
                            answers,
                            locale,
                            new Set(Object.keys(edits)),
                        ),
                    },
                },
                // The save subgraph requires the same tracking-key header as the load.
                context: { headers: { [ROOT_TRACKING_KEY_HEADER]: iaUid } },
            });

            const savedScreenerResult =
                result.data?.smartApplyApplicationState?.updateScreenerQuestionAnswers?.payload
                    ?.screener;
            if (!savedScreenerResult) {
                eventLogger.logReviewError?.({
                    currentPage: location.pathname,
                    errorType: { string: 'screener-questions-itv-error' },
                    errorMessage: { string: 'No screener payload returned from mutation' },
                    draftId: AvroOptional.string(draftId),
                    graphqlOperation: { string: 'SaveSQApplicationData' },
                });
                onFailure();
                return;
            }

            onSuccess();

            // Log-only: do not await — ITV must not delay or change the persist outcome.
            void new ScreenerQuestionITV()
                .execute({ questions, answers, locale, editedNames: Object.keys(edits) }, () =>
                    Promise.resolve(savedScreenerResult),
                )
                .catch((err: unknown) => {
                    // Persist already succeeded — log comparison mismatches and ITV crashes as
                    // itv-error so save-error metrics only represent actual persist failures.
                    eventLogger.logReviewError?.({
                        currentPage: location.pathname,
                        errorType: { string: 'screener-questions-itv-error' },
                        errorMessage: {
                            string: err instanceof Error ? err.message : 'Failed to save answers',
                        },
                        draftId: AvroOptional.string(draftId),
                        graphqlOperation: { string: 'SaveSQApplicationData' },
                    });
                });
        } catch (err) {
            eventLogger.logReviewError?.({
                currentPage: location.pathname,
                errorType: { string: 'screener-questions-save-error' },
                errorMessage: {
                    string: err instanceof Error ? err.message : 'Failed to save answers',
                },
                draftId: AvroOptional.string(draftId),
                graphqlOperation: { string: 'SaveSQApplicationData' },
            });
            onFailure();
        }
    };

    // CTA label + click: "Review application" advances the flow on the main spine; "Update" returns to
    // review from the `/questions/edit` side-trip.
    const footerLabel = inEditSubFlow ? gt.gettext('Update') : gt.gettext('Review application');
    const handleSubmit = () => {
        logButtonClick(inEditSubFlow ? 'update' : 'review_application');
        void persistAndNavigate();
    };

    // The load-error screen has nothing to review — its CTA just skips this dropped draft — so it
    // reads "Next" on the main spine (still "Update" on the edit side-trip, which returns to review).
    const errorFooterLabel = inEditSubFlow ? gt.gettext('Update') : gt.gettext('Next');

    if ((draftLoading || loading) && !data && !loadFailed) {
        return (
            <PageTransition pageKey={`auto-apply-questions-${draftId}`}>
                <Helmet>
                    <title>{copy.autoApplyQuestionsTitle}</title>
                </Helmet>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minBlockSize: '100vh',
                        backgroundColor: 'background',
                    }}
                >
                    <JobInfoHeader
                        fragment={draft}
                        onDelete={handleDeleteDraft}
                        isDeleting={isDeleting}
                    />
                    <Box
                        data-testid="screener-questions-loading"
                        sx={{
                            maxInlineSize: '450px',
                            margin: '0 auto',
                            inlineSize: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            padding: 4,
                        }}
                    >
                        <Skeleton variant="text" sx={{ inlineSize: '70%', alignSelf: 'center' }} />
                        <Skeleton variant="square" sx={{ blockSize: '120px' }} />
                        <Skeleton variant="square" sx={{ blockSize: '120px' }} />
                    </Box>
                </Box>
            </PageTransition>
        );
    }

    if (loadFailed || isCurrentDraftFailed) {
        // Mirror ReviewPage's failed-load pattern: show the message but keep a working escape rather
        // than stranding the jobseeker. There is nothing to answer here, so the CTA navigates
        // unconditionally (no validation, no save) via `continueWithDraftFailed`, which bakes
        // `failedDraftIds` into the destination so it does not race the load-error effect's
        // `markDraftFailed`.
        return (
            <>
                <PageTransition pageKey={`auto-apply-questions-${draftId}`}>
                    <Helmet>
                        <title>{copy.autoApplyQuestionsTitle}</title>
                    </Helmet>
                    {/* Mirror ReviewPage's failed-load layout: the illustration + heading center
                        within the full viewport (100vh, not 100%, so the flex centering has a
                        definite height to resolve against through PageTransition). */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minBlockSize: '100vh',
                            maxInlineSize: '450px',
                            margin: '0 auto',
                            backgroundColor: 'background',
                            paddingBlockEnd: 10,
                            justifyContent: 'center',
                            alignItems: 'center',
                            paddingInline: 4,
                            textAlign: 'center',
                        }}
                    >
                        <Alert9 size="md" aria-hidden sx={{ marginBlockEnd: 4 }} />
                        <Heading level={3}>
                            {gt.gettext('We couldn’t load these questions')}
                        </Heading>
                    </Box>
                </PageTransition>
                <StickyActionFooter>
                    {/* Disabled while the flow is still loading — `nextDraftDestination` reads
                        `allSteps`, which omits the next draft's questions step until the draft-list
                        query resolves, so advancing early could skip that draft's unanswered
                        questions. */}
                    <QuestionsFooterButton
                        label={errorFooterLabel}
                        disabled={!inEditSubFlow && flowLoading}
                        onSubmit={() => {
                            logButtonClick(inEditSubFlow ? 'update' : 'next');
                            // Nothing loaded to save. The edit side-trip returns to review without
                            // dropping the draft; the main spine drops it and skips to the next draft
                            // via continueWithDraftFailed.
                            if (inEditSubFlow) goToReview();
                            else continueWithDraftFailed();
                        }}
                    />
                </StickyActionFooter>
            </>
        );
    }

    return (
        // The FieldValidationProvider wraps both PageTransition and the fixed footer so the jspl
        // fields register and the CTA can read the resulting form validity — while the footer stays
        // OUTSIDE PageTransition, whose transform would otherwise break its position:fixed.
        <FieldValidationProvider>
            <PageTransition pageKey={`auto-apply-questions-${draftId}`}>
                <Helmet>
                    <title>{copy.autoApplyQuestionsTitle}</title>
                    <meta
                        name="description"
                        content={gt.gettext('Answer questions for your automatic job applications')}
                    />
                </Helmet>

                {/* The tabbed content region (sticky JobInfoHeader + TabList, Application and
                    Job details tabs) is shared with ReviewPage via DraftApplicationTabs. The
                    screener question card is the Application tab's content. Controlled so the footer
                    knows which tab is active (to disable "Review application" on Job details). */}
                <DraftApplicationTabs
                    draft={draft}
                    activeIndex={activeTabIndex}
                    onActiveIndexChange={setActiveTabIndex}
                    onDelete={handleDeleteDraft}
                    isDeleting={isDeleting}
                    // Gray field (#F3F2F1) behind the question card — fills the whole tab-content
                    // surface, including any space between the card and the footer.
                    applicationTabBackgroundColor="background.status.generic.weaker"
                >
                    {/* 16px inset so the gray tab-content field (set on DraftApplicationTabs) reads as
                        a margin between the card and the header/tabs, screen edges, and footer. */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '24px',
                            padding: 4,
                        }}
                    >
                        {/* Screener question card */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 5,
                                padding: 4,
                                backgroundColor: 'background',
                                border: '1px solid',
                                borderColor: 'border.weak',
                                borderRadius: 'lg',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 3,
                                    paddingInline: 4,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 3,
                                        borderRadius: 'full',
                                        backgroundColor: 'background.status.warning.weaker',
                                    }}
                                >
                                    <Lock size="md" aria-hidden />
                                </Box>
                                <Heading level={4} sx={{ textAlign: 'center', marginBlock: 0 }}>
                                    {unansweredCount > 0
                                        ? gt.ngettext(
                                              'One more question to answer',
                                              '{0} more questions to answer',
                                              unansweredCount,
                                              [unansweredCount],
                                          )
                                        : gt.gettext('Answer these questions from the employers')}
                                </Heading>
                                <Text level={2} sx={{ textAlign: 'center', marginBlock: 0 }}>
                                    {gt.gettext('Fields marked with an asterisk(*) are required.')}
                                </Text>
                            </Box>

                            <QuestionFields
                                questions={visibleQuestions}
                                answers={answers}
                                loadedAnswers={loadedAnswers}
                                onAnswer={setAnswer}
                                onFileSelect={handleFileSelect}
                                onFileRemove={handleFileRemove}
                            />
                        </Box>

                        {uploadError && (
                            <Alert
                                status="critical"
                                layout="inline"
                                data-testid="screener-questions-upload-error"
                                sx={{ inlineSize: '100%' }}
                            >
                                {gt.gettext('We couldn’t upload your file. Please try again.')}
                            </Alert>
                        )}
                    </Box>
                </DraftApplicationTabs>
            </PageTransition>
            <StickyActionFooter>
                <ValidatedQuestionsFooter
                    label={footerLabel}
                    saving={saving}
                    uploading={uploadInProgress}
                    // Disabled while the flow's step list is still loading — a save failure routes
                    // through nextDraftDestination (allSteps), so block until it's final. The CTA stays
                    // enabled on the Job details tab: submitting from there switches to the Application
                    // tab (see onGoToApplicationTab) so any validation errors are visible.
                    disabled={flowLoading}
                    onGoToApplicationTab={() => setActiveTabIndex(0)}
                    onSubmit={handleSubmit}
                />
            </StickyActionFooter>
        </FieldValidationProvider>
    );
};

export default QuestionsPage;
